import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PackagingDocumentUploadDialogInstructions } from './PackagingDocumentUploadDialogInstructions'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { resolvePackagingDocumentUploadErrorMessage } from '@/shared/lib/detectUnreadableFileContainer'
import { PackagingDocumentUploadPreviewPanel } from './PackagingDocumentUploadPreviewPanel'
import type { PackagingDocumentUploadPreviewPhase } from './PackagingDocumentUploadPreviewPanel'
import {
  createPackagingDocumentPreviewUrl,
  pickSinglePackagingUploadFile,
  revokePackagingDocumentPreviewUrl,
} from './packagingDocumentUploadPreview'
import {
  assessPackagingDocumentUploadEligibility,
  resolvePackagingDocumentUploadIneligibilityMessage,
  type PackagingDocumentUploadEligibility,
} from './assessPackagingDocumentUploadEligibility'
import {
  canUploadPackagingDocumentType,
  PACKAGING_ITEM_DOCUMENT_ACCEPT,
  PACKAGING_ITEM_DOCUMENT_TYPES,
  type PackagingItemDocumentType,
  type PackagingItemDocumentTypeLabels,
} from './packagingDocumentTypes'
import { PackagingDocumentLibraryPicker } from './PackagingDocumentLibraryPicker'
import { PackagingDocumentSameNameReuseAlert } from './PackagingDocumentSameNameReuseAlert'
import {
  findPackagingLibraryDocumentByFileName,
  isPackagingLibraryDocumentAttachedToItem,
} from './packagingDocumentLibraryMatch'
import type { PackagingDocumentLibraryPickerRow } from './PackagingDocumentLibraryPicker'

type UploadPhase = 'pick' | 'confirm' | 'uploading' | 'success'

type PackagingItemDocumentUploadDialogLabels = {
  title: string
  descriptionIntro: string
  requirementFormats: string
  requirementMaxSize: string
  documentType: string
  selectDocumentType: string
  dragDrop: string
  orClick: string
  selectFiles: string
  uploading: string
  uploadFailed: string
  uploadRightsProtected: string
  uploadEncrypted: string
  uploadInvalidPdf: string
  uploadInvalidLegacyXls: string
  uploadFileTooLarge: string
  uploadUnsupportedFileType: string
  uploadEmptyFile: string
  uploadDialogAssessingFile: string
  done: string
  multipleDropped: string
  confirmUpload: string
  chooseDifferentFile: string
  typeAlreadyUploaded: string
  typeLabels: PackagingItemDocumentTypeLabels
  librarySectionTitle: string
  librarySearchPlaceholder: string
  libraryEmpty: string
  libraryEmptyAllLinked: string
  librarySearchEmpty: string
  libraryLinkedItemCount: string
  libraryAttaching: string
  sameNameWarningTitle: string
  sameNameWarningDescription: string
  sameNameAlreadyLinkedDescription: string
  sameNameReuseCta: string
  attachSuccess: string
  uploadSuccess: string
  attachFailed: string
}

type PackagingItemDocumentUploadDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isUploading: boolean
  existingDocumentTypes: PackagingItemDocumentType[]
  labels: PackagingItemDocumentUploadDialogLabels
  libraryDocuments?: PackagingDocumentLibraryPickerRow[]
  itemDocuments?: Array<{ id: string; documentType: string }>
  onUpload: (
    file: File,
    documentType: PackagingItemDocumentType
  ) => Promise<void>
  onAttach?: (
    documentId: string,
    documentType: PackagingItemDocumentType
  ) => Promise<void>
  isAttaching?: boolean
}

export function PackagingItemDocumentUploadDialog({
  open,
  onOpenChange,
  isUploading,
  existingDocumentTypes,
  labels,
  libraryDocuments = [],
  itemDocuments = [],
  onUpload,
  onAttach,
  isAttaching = false,
}: PackagingItemDocumentUploadDialogProps) {
  const [phase, setPhase] = useState<UploadPhase>('pick')
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  )
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    PackagingItemDocumentType | undefined
  >(undefined)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [extraFilesIgnored, setExtraFilesIgnored] = useState(0)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [fileEligibility, setFileEligibility] =
    useState<PackagingDocumentUploadEligibility | null>(null)
  const [isAssessingFile, setIsAssessingFile] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const clearPendingFile = useCallback(() => {
    setPendingFile(null)
    setExtraFilesIgnored(0)
    setFileEligibility(null)
    setIsAssessingFile(false)
    setPreviewUrl((current) => {
      revokePackagingDocumentPreviewUrl(current)
      return null
    })
  }, [])

  useEffect(() => {
    if (!open) {
      setIsProcessing(false)
      setUploadErrorMessage(null)
      setSelectedDocumentType(undefined)
      setPhase('pick')
      setPendingFile(null)
      setExtraFilesIgnored(0)
      setSuccessMessage(null)
      setFileEligibility(null)
      setIsAssessingFile(false)
      setPreviewUrl((current) => {
        revokePackagingDocumentPreviewUrl(current)
        return null
      })
    }
  }, [open])

  const documentTypeItems = PACKAGING_ITEM_DOCUMENT_TYPES.map(
    (documentType) => ({
      value: documentType,
      label: labels.typeLabels[documentType],
    })
  )
  const canUploadSelectedType = canUploadPackagingDocumentType(
    selectedDocumentType,
    existingDocumentTypes
  )
  const showTypeAlreadyUploaded =
    selectedDocumentType != null && !canUploadSelectedType

  const isBusy =
    isUploading ||
    isAttaching ||
    isProcessing ||
    phase === 'uploading' ||
    phase === 'success'
  const showPickZone = phase === 'pick' && pendingFile == null
  const showPreview = pendingFile != null && phase !== 'pick'

  let previewPhase: PackagingDocumentUploadPreviewPhase = 'confirm'
  if (phase === 'success') {
    previewPhase = 'success'
  } else if (phase === 'uploading' || isUploading || isAttaching) {
    previewPhase = 'uploading'
  }

  const availableLibraryDocuments = useMemo(() => {
    if (selectedDocumentType == null) {
      return []
    }
    return libraryDocuments.filter(
      (row) =>
        !isPackagingLibraryDocumentAttachedToItem({
          libraryDocumentId: row.id,
          documentType: selectedDocumentType,
          itemDocuments,
        })
    )
  }, [itemDocuments, libraryDocuments, selectedDocumentType])

  const libraryEmptyListMessage = useMemo(() => {
    if (libraryDocuments.length === 0) {
      return labels.libraryEmpty
    }
    if (availableLibraryDocuments.length === 0) {
      return labels.libraryEmptyAllLinked
    }
    return labels.libraryEmpty
  }, [
    availableLibraryDocuments.length,
    labels.libraryEmpty,
    labels.libraryEmptyAllLinked,
    libraryDocuments.length,
  ])

  const ineligibilityMessages = useMemo(
    () => ({
      uploadEmptyFile: labels.uploadEmptyFile,
      uploadUnsupportedFileType: labels.uploadUnsupportedFileType,
      uploadFileTooLarge: labels.uploadFileTooLarge,
      uploadEncrypted: labels.uploadEncrypted,
      uploadRightsProtected: labels.uploadRightsProtected,
      uploadInvalidPdf: labels.uploadInvalidPdf,
      uploadInvalidLegacyXls: labels.uploadInvalidLegacyXls,
    }),
    [
      labels.uploadEmptyFile,
      labels.uploadEncrypted,
      labels.uploadFileTooLarge,
      labels.uploadInvalidLegacyXls,
      labels.uploadInvalidPdf,
      labels.uploadRightsProtected,
      labels.uploadUnsupportedFileType,
    ]
  )

  const fileIneligibilityMessage = useMemo(() => {
    if (fileEligibility == null || fileEligibility.eligible) {
      return null
    }
    if (fileEligibility.reason == null) {
      return null
    }
    return resolvePackagingDocumentUploadIneligibilityMessage(
      fileEligibility.reason,
      ineligibilityMessages
    )
  }, [fileEligibility, ineligibilityMessages])

  const canConfirmUpload =
    fileEligibility?.eligible === true && !isAssessingFile

  useEffect(() => {
    if (pendingFile == null) {
      setFileEligibility(null)
      setIsAssessingFile(false)
      return
    }

    let cancelled = false
    setIsAssessingFile(true)
    setFileEligibility(null)

    void assessPackagingDocumentUploadEligibility(pendingFile).then((result) => {
      if (!cancelled) {
        setFileEligibility(result)
        setIsAssessingFile(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [pendingFile])

  const sameNameLibraryMatch = useMemo(() => {
    if (pendingFile == null) {
      return undefined
    }
    return findPackagingLibraryDocumentByFileName(
      libraryDocuments,
      pendingFile.name
    )
  }, [libraryDocuments, pendingFile])

  const canReuseSameNameLibraryMatch = useMemo(() => {
    if (sameNameLibraryMatch == null || selectedDocumentType == null) {
      return false
    }
    return !isPackagingLibraryDocumentAttachedToItem({
      libraryDocumentId: sameNameLibraryMatch.id,
      documentType: selectedDocumentType,
      itemDocuments,
    })
  }, [itemDocuments, sameNameLibraryMatch, selectedDocumentType])

  const sameNameWarningDescription = canReuseSameNameLibraryMatch
    ? labels.sameNameWarningDescription
    : labels.sameNameAlreadyLinkedDescription

  const runAttach = useCallback(
    async (libraryDocumentId: string) => {
      if (selectedDocumentType == null || onAttach == null) {
        return
      }
      const attachFromPreview = pendingFile != null
      setUploadErrorMessage(null)
      if (attachFromPreview) {
        setPhase('uploading')
      }
      setIsProcessing(true)
      try {
        await onAttach(libraryDocumentId, selectedDocumentType)
        setSuccessMessage(labels.attachSuccess)
        setPhase('success')
      } catch {
        if (attachFromPreview) {
          setPhase('confirm')
        } else {
          setPhase('pick')
        }
        setUploadErrorMessage(labels.attachFailed)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      labels.attachFailed,
      labels.attachSuccess,
      onAttach,
      pendingFile,
      selectedDocumentType,
    ]
  )

  const handleFilePicked = useCallback(
    (files: File[]) => {
      if (files.length === 0 || selectedDocumentType == null) {
        return
      }
      if (!canUploadSelectedType) {
        return
      }
      const { file, extraFilesIgnored: ignoredCount } =
        pickSinglePackagingUploadFile(files)

      setUploadErrorMessage(null)
      const nextPreviewUrl = createPackagingDocumentPreviewUrl(file)
      setPreviewUrl((current) => {
        revokePackagingDocumentPreviewUrl(current)
        return nextPreviewUrl
      })
      setPendingFile(file)
      setExtraFilesIgnored(ignoredCount)
      setPhase('confirm')
    },
    [canUploadSelectedType, selectedDocumentType]
  )

  const handleConfirmUpload = useCallback(async () => {
    if (
      pendingFile == null ||
      selectedDocumentType == null ||
      !canConfirmUpload
    ) {
      return
    }
    setUploadErrorMessage(null)
    setPhase('uploading')
    setIsProcessing(true)
    try {
      await onUpload(pendingFile, selectedDocumentType)
      setSuccessMessage(labels.uploadSuccess)
      setPhase('success')
    } catch (error) {
      setPhase('confirm')
      setUploadErrorMessage(
        resolvePackagingDocumentUploadErrorMessage(error, {
          uploadFailed: labels.uploadFailed,
          uploadRightsProtected: labels.uploadRightsProtected,
          uploadEncrypted: labels.uploadEncrypted,
          uploadInvalidPdf: labels.uploadInvalidPdf,
          uploadInvalidLegacyXls: labels.uploadInvalidLegacyXls,
          uploadFileTooLarge: labels.uploadFileTooLarge,
          uploadUnsupportedFileType: labels.uploadUnsupportedFileType,
          uploadEmptyFile: labels.uploadEmptyFile,
        })
      )
    } finally {
      setIsProcessing(false)
    }
  }, [
    labels.uploadEmptyFile,
    labels.uploadEncrypted,
    labels.uploadInvalidLegacyXls,
    labels.uploadInvalidPdf,
    labels.uploadFileTooLarge,
    labels.uploadFailed,
    labels.uploadRightsProtected,
    labels.uploadSuccess,
    labels.uploadUnsupportedFileType,
    onUpload,
    pendingFile,
    selectedDocumentType,
    canConfirmUpload,
  ])

  const handleChooseDifferentFile = useCallback(() => {
    setUploadErrorMessage(null)
    setSuccessMessage(null)
    clearPendingFile()
    setPhase('pick')
  }, [clearPendingFile])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files
      if (fileList && fileList.length > 0) {
        handleFilePicked(Array.from(fileList))
      }
      event.target.value = ''
    },
    [handleFilePicked]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      if (isBusy || !canUploadSelectedType || !showPickZone) {
        return
      }
      const fileList = event.dataTransfer.files
      if (fileList && fileList.length > 0) {
        handleFilePicked(Array.from(fileList))
      }
    },
    [canUploadSelectedType, handleFilePicked, isBusy, showPickZone]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])

  const handleDone = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-0 overflow-x-hidden *:min-w-0 sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <PackagingDocumentUploadDialogInstructions
            intro={labels.descriptionIntro}
            requirementFormats={labels.requirementFormats}
            requirementMaxSize={labels.requirementMaxSize}
          />
        </DialogHeader>

        <div className='space-y-1'>
          <Label htmlFor='packaging-document-type-select'>
            {labels.documentType}
          </Label>
          <Select
            value={selectedDocumentType ?? null}
            disabled={phase !== 'pick'}
            modal={false}
            items={documentTypeItems}
            onValueChange={(value) => {
              if (value) {
                setSelectedDocumentType(value as PackagingItemDocumentType)
              }
            }}
          >
            <SelectTrigger
              id='packaging-document-type-select'
              className='w-full'
            >
              <SelectValue placeholder={labels.selectDocumentType}>
                {selectedDocumentType != null
                  ? labels.typeLabels[selectedDocumentType]
                  : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PACKAGING_ITEM_DOCUMENT_TYPES.map((documentType) => (
                <SelectItem key={documentType} value={documentType}>
                  {labels.typeLabels[documentType]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showTypeAlreadyUploaded && (
          <p className='text-sm text-muted-foreground'>
            {labels.typeAlreadyUploaded}
          </p>
        )}

        {uploadErrorMessage && (
          <p className='text-sm text-destructive'>{uploadErrorMessage}</p>
        )}

        {phase === 'success' && pendingFile == null && (
          <div className='space-y-4 rounded-lg border bg-muted/30 p-4'>
            <p className='text-sm'>
              {successMessage ?? labels.attachSuccess}
            </p>
            <div className='flex justify-end'>
              <Button type='button' size='sm' onClick={handleDone}>
                {labels.done}
              </Button>
            </div>
          </div>
        )}

        {showPreview && pendingFile != null && (
          <>
            {phase === 'confirm' && sameNameLibraryMatch != null && (
              <PackagingDocumentSameNameReuseAlert
                fileName={pendingFile.name}
                labels={{
                  title: labels.sameNameWarningTitle,
                  description: sameNameWarningDescription,
                  reuseCta: labels.sameNameReuseCta,
                  attaching: labels.libraryAttaching,
                }}
                isAttaching={isAttaching || isProcessing}
                canReuse={canReuseSameNameLibraryMatch && onAttach != null}
                onReuse={() => {
                  void runAttach(sameNameLibraryMatch.id)
                }}
              />
            )}
            <PackagingDocumentUploadPreviewPanel
              file={pendingFile}
              previewUrl={previewUrl}
              extraFilesIgnored={extraFilesIgnored}
              phase={previewPhase}
              labels={{
                uploading: labels.uploading,
                assessingFile: labels.uploadDialogAssessingFile,
                multipleDropped: labels.multipleDropped,
                confirmUpload: labels.confirmUpload,
                chooseDifferentFile: labels.chooseDifferentFile,
                done: labels.done,
                success: successMessage ?? labels.uploadSuccess,
              }}
              isAssessing={isAssessingFile}
              ineligibilityMessage={fileIneligibilityMessage}
              canConfirmUpload={canConfirmUpload}
              confirmUploadVariant={
                sameNameLibraryMatch != null ? 'outline' : 'default'
              }
              onConfirmUpload={() => {
                void handleConfirmUpload()
              }}
              onChooseDifferentFile={handleChooseDifferentFile}
              onDone={handleDone}
            />
          </>
        )}

        {showPickZone && selectedDocumentType != null && onAttach != null && (
          <PackagingDocumentLibraryPicker
            rows={availableLibraryDocuments}
            typeLabelForRow={(documentType) => {
              const knownType = documentType as PackagingItemDocumentType
              if (PACKAGING_ITEM_DOCUMENT_TYPES.includes(knownType)) {
                return labels.typeLabels[knownType]
              }
              return documentType
            }}
            labels={{
              sectionTitle: labels.librarySectionTitle,
              searchPlaceholder: labels.librarySearchPlaceholder,
              emptyLibrary: labels.libraryEmpty,
              emptySearch: labels.librarySearchEmpty,
              linkedItemCount: labels.libraryLinkedItemCount,
              attaching: labels.libraryAttaching,
            }}
            emptyListMessage={libraryEmptyListMessage}
            disabled={isBusy}
            isAttaching={isAttaching || isProcessing}
            onSelect={(libraryDocumentId) => {
              void runAttach(libraryDocumentId)
            }}
          />
        )}

        {showPickZone && (
          <div
            className={cn(
              'flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
              'border-muted-foreground/25 hover:border-muted-foreground/50',
              (!canUploadSelectedType || (isBusy && phase === 'pick')) &&
                'pointer-events-none opacity-60'
            )}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Upload className='mb-2 size-10 text-muted-foreground' />
            <p className='mb-3 text-sm text-muted-foreground'>
              {labels.dragDrop}
            </p>
            <p className='mb-4 text-xs text-muted-foreground'>{labels.orClick}</p>
            <input
              ref={fileInputRef}
              type='file'
              accept={PACKAGING_ITEM_DOCUMENT_ACCEPT}
              multiple={false}
              className='sr-only'
              onChange={handleInputChange}
              disabled={!canUploadSelectedType}
            />
            <Button
              disabled={!canUploadSelectedType}
              variant='secondary'
              size='sm'
              onClick={() => {
                fileInputRef.current?.click()
              }}
            >
              {labels.selectFiles}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
